import { faCaretDown, faX } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  Autocomplete,
  Button,
  DropdownMenu,
  FormControl,
  Select,
  Text,
  TextField,
  View
} from '@zinc/ui';

import type { ReactNode } from 'react';

import { createRef, forwardRef, useCallback, useMemo, useState } from 'react';

import { cache } from 'tm3-core/cache';

import type IFSDProps from '../field-select-filter-dynamic/iprops';
import FieldSelectItemDefaultTemplate, {
  BoldItemKeyCtx,
  DisplayMemberCtx,
  ItemStartSlotCtx,
  OnLocalChangeCtx,
  SecondaryDisplayMemberCtx,
  ValueMemberCtx
} from './controls/item-template';
import type IProps from './iprops';
import type { IFSProps } from './iprops';
import { VirtualList } from './VirtualList';

export type FullProps = IProps & (IFSProps | IFSDProps);

export enum EReason {
  blur,
  filter,
  select
}

function FieldSelect(props: FullProps) {
  const {
    value,
    onChange,
    onFocus,
    dLabel,
    label,
    items = [],
    valueMember = 'payload',
    displayMember = 'text',
    secondaryDisplayMember = undefined,
    submitted,
    disabled,
    placeholder,
    variant = 'outline',
    includeNone = false,
    noneText = 'None Selected',
    noneValue,
    errorText,
    filterable,
    lookup,
    dynamic = false,
    startSlot,
    dataTestId,
    lookupItems,
    className,
    removeLegacyPadding = false,
    size,
    color = 'neutral',
    boldItemKey,
    inputAttributes,
    appendMode = false,
    itemStartSlot,
    selectedStartSlot,
    selectedDisplayFormatter,
    itemHeight,
    ...other
  } = props;

  const ItemTemplate = props.itemTemplate;

  const computedLabel = useMemo(
    () => (dLabel ? (cache.dynamicLabels.GetLabel(dLabel)?.text ?? label ?? '') : label),
    [label, dLabel]
  );
  let options = items?.map((item) =>
    String.isString(item) ? { payload: item, text: item } : item
  );

  if (lookup) {
    const values = lookupItems || cache.lookups.GetLookups(lookup)?.values;
    options = values ? values.map((x) => ({ payload: x, text: x })) : [];
  }

  if (includeNone) {
    options = [
      {
        [displayMember]: noneText,
        [valueMember]: 'noneValue' in props ? noneValue : -1
      },
      ...(options ?? [])
    ];
  }

  const [filter, setFilter] = useState<string | null>(null);

  const displayValue = useMemo(() => {
    if (!filterable) {
      return filter ?? value;
    }

    const item = items?.find((x) => x[valueMember]?.toString() === value?.toString());
    return dynamic
      ? (filter ?? item?.[displayMember] ?? value)
      : (item?.[displayMember] ?? filter ?? value);
  }, [displayMember, dynamic, filter, filterable, items, value, valueMember]);

  let control: ReactNode = null;
  const filteredItems = useMemo(() => {
    if (!filter) return options;
    if (appendMode) return options;
    const normalizedFilter = (filter ?? '').toLowerCase().trim();
    const filterTerms = normalizedFilter.split(/\s+/);
    return options.filter((item) => {
      const primaryText = (item?.[displayMember] ?? '').toLowerCase() || '';
      const secondaryText = secondaryDisplayMember
        ? item[secondaryDisplayMember]?.toLowerCase() || ''
        : '';

      const matchesFilter = (text: string) =>
        filterTerms.every((term) => text.split(/\s+/).some((word) => word.startsWith(term)));
      return matchesFilter(primaryText) || matchesFilter(secondaryText);
    });
  }, [options, displayMember, secondaryDisplayMember, filter, appendMode]);

  const virtualListItemLength = useMemo(
    () => `${findMaxLengthByProp(items, displayMember) * 0.5}rem`,
    [items, displayMember]
  );

  const onChangeLocal = useCallback(
    (e) => {
      if (filterable)
        onChange?.(e.target.value); //grim hack to maintain compatability
      else onChange?.(e);
    },
    [onChange, filterable]
  );

  const inputRef = createRef<HTMLInputElement>();
  if (dynamic) {
    control = (
      <Autocomplete
        color={color}
        value={filter != null ? filter : displayValue}
        variant={variant}
        suffix={
          <FontAwesomeIcon
            icon={faCaretDown}
            onClick={(e) => {
              try {
                e.currentTarget?.parentNode?.parentNode?.parentNode?.childNodes[0]?.focus();
              } catch {}
            }}
          />
        }
        onBlur={(e) => {
          setTimeout(() => {
            if (dynamic) {
              if (filter) onChange(filter, EReason.blur);
            }
            setFilter(null);
          }, 1000);
        }}
        onChange={(e) => {
          onChange(e.value, EReason.filter);
          if (!appendMode) {
            setFilter(e.value);
          }
        }}
        onItemSelect={(e) => {
          setFilter(null);
          if (appendMode && inputRef.current) {
            insertTextAtCaret(inputRef.current, e.value, (x) => {
              setFilter(x);
              onChange(x, EReason.select);
            });
          } else {
            onChange(e.value, EReason.select);
          }
        }}
        placeholder={placeholder ?? computedLabel}
        inputAttributes={{
          autoComplete: 'off',
          'data-testid': dataTestId ?? label,
          ref: inputRef,
          ...inputAttributes
        }}
        hasError={submitted && !!errorText}
        className={`${className}  ${errorText && !submitted ? 'border !border-orange-300' : 'border'}`}
        startSlot={startSlot}
      >
        {!appendMode && filter && filteredItems.length === 0 && (
          <View padding={2}>
            <div style={{ width: virtualListItemLength }}>
              <Text>No Results Found</Text>
            </div>
          </View>
        )}

        <VirtualList
          height={300}
          itemHeight={itemHeight ?? (secondaryDisplayMember ? 44 : 35)}
          items={filteredItems}
          virtualContainerWidth={ItemTemplate ? undefined : virtualListItemLength}
          ItemTemplate={forwardRef(({ item, style, ...other }, ref) => {
            return (
              <Autocomplete.Item
                key={item[valueMember]}
                value={item[valueMember]}
                attributes={{ style, ref, ...other }}
                stopPropagation={true}
              >
                {ItemTemplate && <ItemTemplate item={item} />}
                {!ItemTemplate && (
                  <>
                    <Text className="text-ellipsis text-nowrap">{item[displayMember]}</Text>
                    {secondaryDisplayMember && (
                      <Text variant="caption-1" color="neutral-faded" className="mb-[-8px]">
                        {item[secondaryDisplayMember]}
                      </Text>
                    )}
                  </>
                )}
              </Autocomplete.Item>
            );
          })}
        />
      </Autocomplete>
    );
  } else {
    const selected = options?.find((x) => x[valueMember] === value);

    const displayValue = selected?.[displayMember] ?? '';
    const displayText =
      typeof displayValue === 'string'
        ? (selectedDisplayFormatter?.(displayValue) ?? displayValue)
        : displayValue;
    const resolvedStartSlot =
      selectedStartSlot && selected ? selectedStartSlot(selected) : startSlot;
    const startSlotComp = resolvedStartSlot ? (
      <View gap={1} direction="row" align="center">
        {resolvedStartSlot}
      </View>
    ) : null;
    control = (
      <>
        <DropdownMenu
          trapFocusMode="selection-menu"
          position="bottom-start"
          fallbackPositions={['bottom-end', 'top-start', 'top-end']}
        >
          <DropdownMenu.Trigger>
            {(attributes) => (
              <>
                <Select
                  color={color}
                  variant={variant}
                  placeholder={!displayValue && (placeholder ?? noneText ?? computedLabel)}
                  inputAttributes={{ ...attributes, content: displayText }}
                  hasError={submitted && !!errorText}
                  className={
                    className +
                    ' ' +
                    (errorText && !submitted ? 'border !border-orange-300' : 'border')
                  }
                  startSlot={startSlotComp}
                  data-testid={dataTestId ?? label}
                  size={size}
                  onFocus={onFocus}
                  attributes={{
                    'data-testid': dataTestId ?? label
                  }}
                >
                  {displayValue}
                </Select>
              </>
            )}
          </DropdownMenu.Trigger>
          <DropdownMenu.Content className="!w-[fit-content] !min-w-[none] !max-w-[none] !overflow-x-visible">
            {options.length > 10 && (
              <View paddingBottom={2}>
                <TextField
                  name="filter"
                  placeholder="Search in list"
                  value={filter ?? ''}
                  inputAttributes={{ autoComplete: 'off' }}
                  endSlot={
                    filter && (
                      <Button
                        variant="ghost"
                        size="small"
                        icon={<FontAwesomeIcon icon={faX} />}
                        onClick={() => setFilter(null)}
                      />
                    )
                  }
                  attributes={{ autoFocus: true }}
                  onChange={({ value }) => setFilter(value)}
                />
              </View>
            )}
            {filteredItems.length === 0 && (
              <View padding={2}>
                <div style={{ width: virtualListItemLength }}>
                  <Text color="neutral-faded">No items found</Text>
                </div>
              </View>
            )}
            <OnLocalChangeCtx.Provider value={onChangeLocal}>
              <ValueMemberCtx.Provider value={valueMember}>
                <BoldItemKeyCtx.Provider value={boldItemKey}>
                  <DisplayMemberCtx.Provider value={displayMember}>
                    <SecondaryDisplayMemberCtx.Provider value={secondaryDisplayMember}>
                      <ItemStartSlotCtx.Provider value={itemStartSlot}>
                        <VirtualList
                          height={300}
                          itemHeight={itemHeight ?? (secondaryDisplayMember ? 44 : 35)}
                          items={filteredItems}
                          virtualContainerWidth={virtualListItemLength}
                          ItemTemplate={ItemTemplate ?? FieldSelectItemDefaultTemplate}
                        />
                      </ItemStartSlotCtx.Provider>
                    </SecondaryDisplayMemberCtx.Provider>
                  </DisplayMemberCtx.Provider>
                </BoldItemKeyCtx.Provider>
              </ValueMemberCtx.Provider>
            </OnLocalChangeCtx.Provider>
          </DropdownMenu.Content>
        </DropdownMenu>
      </>
    );
  }

  return (
    <View paddingBottom={removeLegacyPadding ? 0 : 2}>
      <FormControl disabled={disabled} hasError={!!errorText && submitted}>
        {computedLabel && <FormControl.Label>{computedLabel}</FormControl.Label>}
        {control}
        {errorText && submitted && <FormControl.Error>{errorText}</FormControl.Error>}
      </FormControl>
    </View>
  );
}

export default FieldSelect;

function findMaxLengthByProp(arr, prop) {
  var max = 30; // effectively our min width for a select
  if (!arr) return max;
  for (let item of arr) {
    const value = item?.[prop];
    if (value && typeof value === 'string' && value.length > max) {
      max = value.length;
    }
  }
  return max;
}

function insertTextAtCaret(
  inputEl: HTMLInputElement,
  text: string,
  onChange: (value: string) => void
) {
  if (!inputEl) return;

  let start = inputEl.selectionStart ?? inputEl.value?.length ?? 0;
  const end = inputEl.selectionEnd ?? inputEl.value?.length ?? 0;

  const currentValue = inputEl.value;
  let before = currentValue.substring(0, start);
  if (before.length > 0 && before[before.length - 1] !== ' ') {
    // Ensure space before inserting text
    before += ' ';
    start += 1; // Adjust start position due to added space
  }
  const newValue = before + text + currentValue.substring(end);

  inputEl.value = newValue;

  // Move caret to just after the inserted text
  const newCaretPosition = start + text.length;
  onChange?.(newValue);
  requestAnimationFrame(() => {
    inputEl.setSelectionRange(newCaretPosition, newCaretPosition);
  });
}
